package fyi.moko.hotpl.domain.service

/**
 * Records what the person actually does in the app — which chips get tapped, which cues
 * get opened, where a session ends — as a per-user event stream the developer can read
 * back as a timeline.
 *
 * ── 왜 Firebase Analytics가 아니라 자체 로그인가 ──────────────────────────────
 *  · 사용자가 둘(개발자 + 리뷰어)이다. GA4는 수천 명의 집계를 보는 도구라 N=2에서는
 *    임계치에 걸려 비고, 24~48시간 늦으며, 원시 이벤트는 BigQuery를 붙여야 보인다.
 *    필요한 것은 "리뷰어가 어제 저녁에 어떤 순서로 뭘 눌렀나"라는 개인별 원시 타임라인이다.
 *  · 리뷰어의 말은 기억에 기대지만 로그는 기대지 않는다 — "왜"는 못 알려 줘도 "언제
 *    어디서"는 정확하다. 그걸 들고 물으면 대답이 나온다.
 *  · 이 로그로 UI를 자동으로 바꾸지 않는다. 사람이 읽고 어떤 기능을 어느 층에 둘지
 *    정하는 근거로만 쓴다(적응형 메뉴는 손이 기억한 위치를 깬다).
 *
 * [track] is fire-and-forget and never throws: a failed write must not take the screen
 * down. Implementations decide whether anything is actually sent (see
 * [UsageTrackingPreference]).
 */
interface UsageTracker {
    fun track(name: String, params: Map<String, String> = emptyMap())
}

/** For tests and DI stubs that want nothing recorded. */
object NoOpUsageTracker : UsageTracker {
    override fun track(name: String, params: Map<String, String>) = Unit
}

/**
 * The event vocabulary, kept in one place so the report script and the screens agree.
 * Names are stable identifiers — renaming one splits the history.
 */
object UsageEvents {
    /** Once per process, from the tracker itself: `build`, `platform`. */
    const val SESSION_START = "session_start"

    /** A screen or Home tab came into view: `name` (home/journal, detail, add, …). */
    const val SCREEN = "screen"

    /** A Journal filter/sort chip: `chip` (been, want_to_go, nearest, az, recent, liked, category:X, tag), `on`. */
    const val FILTER_TAP = "filter_tap"
    const val FILTERS_CLEAR = "filters_clear"

    /** Search panel opened/closed: `open`. */
    const val SEARCH_TOGGLE = "search_toggle"

    /** Journal switched between list and map: `map`. */
    const val MAP_TOGGLE = "map_toggle"

    /** The recall/revisit/recharge cue block was collapsed or expanded: `expanded`. */
    const val CUES_TOGGLE = "cues_toggle"

    /** A cue actually led somewhere: `kind` (recall, revisit, recharge). */
    const val CUE_OPEN = "cue_open"

    /** A feeling chosen in the recharge picker: `emotion`, `on`. */
    const val RECHARGE_PICK = "recharge_pick"

    /** An item in the Home overflow menu: `item`. */
    const val MENU_TAP = "menu_tap"

    /** The list went empty while filters/search were active — the R-14 frustration signal. */
    const val EMPTY_RESULTS = "empty_results"

    /** The Add-place entry point was used: `from` (fab, nav_bar). */
    const val ADD_PLACE_TAP = "add_place_tap"

    /** Something done on the Detail screen: `kind` (log_visit_open, visit_saved, liked, delete, visibility, edit, share, open_maps, photo_open). */
    const val DETAIL_ACTION = "detail_action"

    /** Something done on the Friends tab: `kind` (mutual_share). */
    const val FRIENDS_ACTION = "friends_action"

    /** The tracking switch itself was flipped: `enabled`. Sent even when turning off, as the last event. */
    const val USAGE_TRACKING = "usage_tracking"
}
